import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, Alert, Stack, Select, MenuItem, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useYouTubeIframeAPI } from '@/hooks/useYouTubeIframeAPI';
import { useVideoPlayerStore } from '@/store/videoPlayerStore';
import type { VideoPlayerState } from '@/store/videoPlayerStore';
import { PLAYBACK_SPEEDS } from '@/config/constants';

/** Programmatic API exposed to parent components via a forwarded ref (AC 4). */
export interface YouTubePlayerRef {
  /**
   * Seek to a specific position in seconds.
   * Delegates to YT.Player.seekTo(seconds, true) (allowSeekAhead = true).
   */
  seekTo(seconds: number): void;
  /** Resume playback. Delegates to YT.Player.playVideo(). */
  play(): void;
  /** Pause playback. Delegates to YT.Player.pauseVideo(). */
  pause(): void;
}

interface YouTubePlayerProps {
  /** External YouTube video ID used ONLY to initialise the IFrame player. */
  youtubeVideoId: string;
  /** Video title used for the iframe's title attribute (accessibility). */
  title: string;
  /** Optional callback fired when the player's current time changes. */
  onTimeUpdate?: (currentTime: number) => void;
  /** Optional callback fired when the player state changes. */
  onStateChange?: (state: VideoPlayerState) => void;
  /**
   * Optional callback fired once when the underlying YT.Player instance's onReady event
   * fires — the earliest point at which imperative ref calls (seekTo/play/pause) are
   * guaranteed to reach a live player rather than a no-op/null ref.
   */
  onReady?: () => void;
  /**
   * The currently selected segment for loop-boundary seeking (Story 2.3).
   * When isLooping is true and playback reaches/passes end_time, the player
   * seeks back to start_time and continues playing.
   */
  selectedSegment?: { id: number; start_time: number; end_time: number } | null;
  /** Whether loop mode is active (Story 2.3). */
  isLooping?: boolean;
  /**
   * Optional callback fired once per loop-boundary restart — when playback reaches
   * the selected segment's end_time while isLooping is true and the player seeks
   * back to start_time. Fired with the looping segment's id. Does NOT fire on the
   * initial play or on non-loop pauses/seeks (matches AudioPlayer semantics: every
   * loop iteration counts as a play).
   */
  onLoopIteration?: (segmentId: number) => void;
  /** Optional content rendered right-aligned in the speed control row. */
  endAction?: ReactNode;
}

/**
 * Map YouTube IFrame API PlayerState integers to our normalised VideoPlayerState.
 *
 * Architecture mapping (Story 2.4 reconciliation):
 *   -1 (UNSTARTED) → 'idle'
 *    0 (ENDED)     → 'idle'  (architecture normalises ended to idle; see story 2.4 task 2.4)
 *    1 (PLAYING)   → 'playing'  (or 'ad' — see resolveState below)
 *    2 (PAUSED)    → 'paused'
 *    3 (BUFFERING) → 'buffering'
 *    5 (CUED)      → 'idle'
 *
 * NOTE: mapYTState never emits 'ended'. The 'ended' union member is retained in
 * VideoPlayerState for any future direct writes, but the IFrame path normalises
 * ENDED (0) to 'idle' to match architecture intent.
 */
function mapYTState(ytState: number): VideoPlayerState {
  switch (ytState) {
    case 1:
      return 'playing';
    case 2:
      return 'paused';
    case 3:
      return 'buffering';
    case 0:
      // Architecture maps ENDED → 'idle' (story 2.4 task 2.4 reconciliation).
      return 'idle';
    default:
      // -1 (UNSTARTED) and 5 (CUED) both map to idle.
      return 'idle';
  }
}

/**
 * Resolve the true player state from a raw YT.PlayerState event.
 *
 * Ad detection: when the API fires PLAYING (1) but getDuration() returns 0, the player
 * is showing an ad (the ad's duration is not exposed like a regular video). In that
 * case we emit 'ad' instead of 'playing' so timing-dependent behaviours (loop boundary,
 * listening-time) can pause automatically.
 */
function resolveState(
  ytState: number,
  getDuration: () => number,
): VideoPlayerState {
  if (ytState === 1 && getDuration() === 0) {
    return 'ad';
  }
  return mapYTState(ytState);
}

/**
 * YouTubePlayer — primary IFrame wrapper for Story 2.1.
 *
 * - Initialises the YouTube IFrame API player after the script is ready.
 * - Destroys the player instance on unmount (no memory leaks).
 * - Does NOT autoplay on mount — play requires explicit user interaction (AC 1, 6).
 * - Writes playerState and currentTime to videoPlayerStore (Task 3.2).
 * - Renders a loading skeleton while the player is initialising (Task 2.7).
 * - Renders a clear inline error when the IFrame API script fails to load (Task 2.7, AC 5).
 * - Exposes a playback-rate control (AC 3).
 * - Exposes seekTo(seconds) through a forwarded ref (AC 4).
 * - Uses ref-managed player instance — does not re-mount unnecessarily.
 */
export const YouTubePlayer = forwardRef<YouTubePlayerRef, YouTubePlayerProps>(
  ({ youtubeVideoId, title, onTimeUpdate, onStateChange, onReady, selectedSegment, isLooping, onLoopIteration, endAction }, forwardedRef) => {
  const { t } = useTranslation('player');
  const { ready: apiReady, error: apiError } = useYouTubeIframeAPI();

  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const timeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const setPlayerState = useVideoPlayerStore((s) => s.setPlayerState);
  const setCurrentTime = useVideoPlayerStore((s) => s.setCurrentTime);
  const reset = useVideoPlayerStore((s) => s.reset);

  // Stable refs so closures inside the YT.Player constructor never capture stale values.
  const onStateChangeRef = useRef(onStateChange);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onReadyRef = useRef(onReady);
  const onLoopIterationRef = useRef(onLoopIteration);
  const titleRef = useRef(title);
  // Loop state refs — updated via effects so the polling interval always reads current values
  // without needing to re-create the interval when these props change (Story 2.3).
  const selectedSegmentRef = useRef<{ id: number; start_time: number; end_time: number } | null>(selectedSegment ?? null);
  const isLoopingRef = useRef(isLooping ?? false);
  useEffect(() => { onStateChangeRef.current = onStateChange; }, [onStateChange]);
  useEffect(() => { onTimeUpdateRef.current = onTimeUpdate; }, [onTimeUpdate]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  useEffect(() => { onLoopIterationRef.current = onLoopIteration; }, [onLoopIteration]);
  useEffect(() => { titleRef.current = title; }, [title]);
  useEffect(() => { selectedSegmentRef.current = selectedSegment ?? null; }, [selectedSegment]);
  useEffect(() => { isLoopingRef.current = isLooping ?? false; }, [isLooping]);

  // Start/clear the time-polling interval.
  const startTimePolling = useCallback(() => {
    if (timeIntervalRef.current) return;
    timeIntervalRef.current = setInterval(() => {
      if (playerRef.current) {
        const t = playerRef.current.getCurrentTime();
        setCurrentTime(t);
        onTimeUpdateRef.current?.(t);
        // Segment boundary handling: when playback reaches or passes the selected
        // segment's end time, either loop back to start (loop mode) or pause (normal mode).
        // selectedSegmentRef and isLoopingRef are refs so they never go stale in this closure.
        const seg = selectedSegmentRef.current;
        if (seg != null && t >= seg.end_time) {
          if (isLoopingRef.current) {
            playerRef.current.seekTo(seg.start_time, true);
            onLoopIterationRef.current?.(seg.id);
          } else {
            playerRef.current.pauseVideo();
            playerRef.current.seekTo(seg.end_time, true);
          }
        }
      }
    }, 500);
  }, [setCurrentTime]);

  const stopTimePolling = useCallback(() => {
    if (timeIntervalRef.current) {
      clearInterval(timeIntervalRef.current);
      timeIntervalRef.current = null;
    }
  }, []);

  // Initialise player once the IFrame API script is ready.
  useEffect(() => {
    if (!apiReady || !containerRef.current) return;

    const player = new window.YT.Player(containerRef.current, {
      videoId: youtubeVideoId,
      playerVars: {
        autoplay: 0,      // AC 1, 6: no autoplay — explicit user action required
        controls: 1,      // Show native YouTube controls
        rel: 0,           // Don't show related videos from other channels at end
        modestbranding: 1,
        playsinline: 1,   // AC 6: required for iOS Safari inline playback
      },
      events: {
        onReady: () => {
          // Set the required title attribute on the actual <iframe> element (accessibility).
          // The YT API replaces the container div's content with an <iframe>; find it via ref.
          const iframe = containerRef.current?.querySelector('iframe');
          if (iframe) {
            iframe.setAttribute('title', `YouTube video player — ${titleRef.current}`);
          }
          setPlayerState('idle');
          onReadyRef.current?.();
        },
        onStateChange: (event) => {
          // resolveState handles both ad detection (getDuration() === 0) and
          // the 'ended' → 'idle' normalisation from story 2.4 task 2.4.
          const normalized = resolveState(
            event.data,
            () => event.target?.getDuration?.() ?? -1,
          );
          setPlayerState(normalized);
          onStateChangeRef.current?.(normalized);

          if (normalized === 'playing') {
            startTimePolling();
          } else {
            stopTimePolling();
            // Capture final position on pause/end.
            if (event.target) {
              const t = event.target.getCurrentTime();
              setCurrentTime(t);
              onTimeUpdateRef.current?.(t);
            }
          }
        },
        onError: () => {
          setPlayerState('idle');
          stopTimePolling();
        },
      },
    });

    playerRef.current = player;

    return () => {
      stopTimePolling();
      // Destroy is async-safe — YouTube IFrame API marks the instance for cleanup
      try {
        player.destroy();
      } catch {
        // Ignore destroy errors during unmount
      }
      playerRef.current = null;
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiReady, youtubeVideoId]);

  // Expose imperative controls via the forwarded ref (AC 4, Task 4.1).
  useImperativeHandle(forwardedRef, () => ({
    seekTo(seconds: number) {
      playerRef.current?.seekTo(seconds, true);
    },
    play() {
      playerRef.current?.playVideo();
    },
    pause() {
      playerRef.current?.pauseVideo();
    },
  }));

  const handleSpeedChange = useCallback((event: SelectChangeEvent<number>) => {
    const rate = Number(event.target.value);
    playerRef.current?.setPlaybackRate(rate);
  }, []);

  // Error state — IFrame API script failed to load
  if (apiError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('playerLoadFailed')}
      </Alert>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Loading skeleton — displayed until the IFrame API script is ready */}
      {!apiReady && (
        <Skeleton
          variant="rectangular"
          width="100%"
          sx={{ aspectRatio: '16/9', maxHeight: { md: '40vh' }, display: 'block' }}
          aria-label={t('playerLoading')}
        />
      )}

      {/* IFrame container — always rendered so the ref is available for YT.Player init.
          Hidden via CSS until the API is ready to avoid layout shift. */}
      <Box
        sx={{
          width: '100%',
          aspectRatio: '16/9',
          maxHeight: { md: '40vh' },
          display: apiReady ? 'block' : 'none',
          '& iframe': {
            width: '100%',
            height: '100%',
            border: 'none',
          },
        }}
      >
        <Box
          ref={containerRef}
          sx={{ width: '100%', height: '100%' }}
          aria-label={`YouTube video player — ${title}`}
        />
      </Box>

      {/* Playback speed control */}
      {apiReady && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}
        >
          <Typography variant="body2" color="text.secondary" id="speed-label">
            {t('speed')}
          </Typography>
          <Select<number>
            size="small"
            defaultValue={1.0}
            onChange={handleSpeedChange}
            inputProps={{ 'aria-labelledby': 'speed-label' }}
            sx={{
              minWidth: 80,
              '& .MuiInputBase-root': {
                minHeight: 44,
              },
              '& .MuiSelect-select': {
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            {PLAYBACK_SPEEDS.map((rate) => (
              <MenuItem key={rate} value={rate}>
                {rate}×
              </MenuItem>
            ))}
          </Select>
          {endAction && <Box sx={{ ml: 'auto' }}>{endAction}</Box>}
        </Stack>
      )}
    </Box>
  );
});

YouTubePlayer.displayName = 'YouTubePlayer';
